import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  CardContent,
  Typography,
  Box,
  Divider,
  IconButton,
  Button,
  Link,
  CircularProgress,
  Alert,
  Snackbar,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { blockService, mapGistService } from '../services/api';
import { BlockNodeGraphResponse, BlockSequence } from '../types/api';
import BlockSequenceDisplay, { prepareForDisplay } from './BlockSequenceDisplay';
import BlockNodeGraph from './BlockNodeGraph';
import MarkdownText, { MarkdownRenderer } from './MarkdownText';
import HoverActionIconButton from './HoverActionIconButton';
import { getCategoryHexColor } from '../utils/habitColors';

const SQUARE_SIZE = 144;

// ─── Scrollbar styles (shared) ────────────────────────────────────────────────

const scrollbarSx = {
  overflowX: 'auto',
  width: '100%',
  minWidth: 0,
  pb: 1,
  '&::-webkit-scrollbar': { height: 8 },
  '&::-webkit-scrollbar-track': { backgroundColor: '#f0efed', borderRadius: 4 },
  '&::-webkit-scrollbar-thumb': { backgroundColor: '#c2bfba', borderRadius: 4 },
  '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#a09d98' },
};

// ─── Types ────────────────────────────────────────────────────────────────────

export interface WallBlock {
  id: string;
  label: string;
  color?: string; // hex color derived from the sequence's habit category
}

/** A single horizontal row on the Block Wall. rowKsuid is null for unsaved new rows. */
export interface WallRow {
  rowKsuid: string | null;
  blocks: WallBlock[];
}

export type DragSource =
  | { type: 'source'; block: WallBlock }
  | { type: 'wall'; rowIndex: number; index: number };

// ─── Horizontal wheel-scroll hook ─────────────────────────────────────────────

function useHorizontalWheelScroll() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (e.deltaY === 0) return;
      // Only redirect vertical scroll to horizontal when the row actually overflows.
      if (el.scrollWidth <= el.clientWidth) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);
  return ref;
}

// ─── DropZone ─────────────────────────────────────────────────────────────────

const DropZone: React.FC<{
  index: number;
  active: boolean;
  enabled: boolean;
  onDragOver: (index: number) => void;
  onDragLeave: () => void;
  onDrop: (index: number) => void;
}> = ({ index, active, enabled, onDragOver, onDragLeave, onDrop }) => (
  <Box
    onDragOver={(e) => { if (enabled) { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'move'; onDragOver(index); } }}
    onDragLeave={(e) => { e.stopPropagation(); onDragLeave(); }}
    onDrop={(e) => { if (enabled) { e.preventDefault(); e.stopPropagation(); onDrop(index); } }}
    sx={{
      width: 32,
      minWidth: 32,
      height: SQUARE_SIZE,
      flexShrink: 0,
      position: 'relative',
      cursor: enabled ? 'copy' : 'default',
    }}
  >
    {active && (
      <Box sx={{
        position: 'absolute',
        left: '50%',
        top: 0,
        bottom: 0,
        width: 4,
        transform: 'translateX(-50%)',
        bgcolor: 'primary.main',
        borderRadius: 1,
      }} />
    )}
  </Box>
);

// ─── SquareRow (source rows, edit-mode only) ──────────────────────────────────

const SquareRow: React.FC<{
  sequence: BlockSequence;
  wallBlockIds: Set<string>;
  dragRef: React.MutableRefObject<DragSource | null>;
}> = ({ sequence, wallBlockIds, dragRef }) => (
  <BlockSequenceDisplay
    orientation="horizontal"
    sequence={sequence}
    wallBlockIds={wallBlockIds}
    dragRef={dragRef}
  />
);

// ─── BlockWall ────────────────────────────────────────────────────────────────

const BlockWall: React.FC<{
  rowIndex: number;
  blocks: WallBlock[];
  onBlocksChange: (rowIndex: number, blocks: WallBlock[]) => void;
  dragRef: React.MutableRefObject<DragSource | null>;
  blockSequences: BlockSequence[];
  /** All block IDs currently on ANY row of the wall — used for predecessor validation. */
  allWallIds: Set<string>;
  onInvalidDrop: (message: string) => void;
  /** Called after a wall-block dragStart so the parent can attach crossRowMove. */
  onWallDragStart: (src: DragSource & { crossRowMove?: (toRow: number, toInsert: number) => void }) => void;
  isEditing: boolean;
}> = ({ rowIndex, blocks, onBlocksChange, dragRef, blockSequences, allWallIds, onInvalidDrop, onWallDragStart, isEditing }) => {
  const scrollRef = useHorizontalWheelScroll();
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleDrop = useCallback(
    (insertAt: number) => {
      const src = dragRef.current;
      if (!src) return;
      if (src.type === 'source') {
        if (allWallIds.has(src.block.id)) { setDragOverIndex(null); return; }
        // Validate that all predecessor blocks are already on the wall.
        const [seqId, blockId] = src.block.id.split('#');
        const seq = blockSequences.find((s) => s.blockSequenceId === seqId);
        if (seq) {
          const blockIdx = seq.blocks.findIndex((b) => b.blockId === blockId);
          for (let i = 0; i < blockIdx; i++) {
            const predId = `${seqId}#${seq.blocks[i].blockId}`;
            if (!allWallIds.has(predId)) {
              onInvalidDrop(`You must add "${seq.blocks[i].text || `Block ${i + 1}`}" to the wall before this block.`);
              setDragOverIndex(null);
              dragRef.current = null;
              return;
            }
          }
        }
        const next = [...blocks];
        next.splice(insertAt, 0, src.block);
        onBlocksChange(rowIndex, next);
      } else {
        // Wall-to-wall drop.
        if (src.rowIndex === rowIndex) {
          // Same-row reorder.
          const fromIdx = src.index;
          if (fromIdx === insertAt || fromIdx === insertAt - 1) { setDragOverIndex(null); return; }
          const next = [...blocks];
          const [item] = next.splice(fromIdx, 1);
          const adj = fromIdx < insertAt ? insertAt - 1 : insertAt;
          next.splice(adj, 0, item);
          onBlocksChange(rowIndex, next);
        } else {
          // Cross-row move: remove from source row and insert here.
          // We call onBlocksChange with a special sentinel to signal cross-row;
          // handled by parent via a dedicated cross-row handler stored in dragRef.
          // Instead, we use a callback pattern: dragRef carries a crossRowMove fn.
          if ((dragRef as React.MutableRefObject<DragSource & { crossRowMove?: (toRowIndex: number, toInsertAt: number) => void } | null>).current?.crossRowMove) {
            (dragRef as React.MutableRefObject<DragSource & { crossRowMove?: (toRowIndex: number, toInsertAt: number) => void } | null>).current!.crossRowMove!(rowIndex, insertAt);
          }
        }
      }
      setDragOverIndex(null);
      dragRef.current = null;
    },
    [blocks, dragRef, onBlocksChange, rowIndex, allWallIds, blockSequences, onInvalidDrop],
  );

  const clearDragOver = useCallback(() => setDragOverIndex(null), []);

  if (blocks.length === 0) {
    return (
      <Box
        onDragOver={(e) => { if (isEditing) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverIndex(0); } }}
        onDragLeave={clearDragOver}
        onDrop={(e) => { if (isEditing) { e.preventDefault(); handleDrop(0); } }}
        sx={{
          minHeight: SQUARE_SIZE,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: dragOverIndex !== null
            ? '2px dashed rgba(50, 202, 29, 0.65)'
            : 'none',
          borderRadius: 1,
          transition: 'border 0.12s ease',
        }}
      >
        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', userSelect: 'none' }}>
          Empty Block Wall. Edit to add blocks.
        </Typography>
      </Box>
    );
  }

  return (
    <Box ref={scrollRef} sx={scrollbarSx}>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: 'max-content' }}>
        <DropZone index={0} active={dragOverIndex === 0} enabled={isEditing} onDragOver={isEditing ? setDragOverIndex : () => {}} onDragLeave={clearDragOver} onDrop={isEditing ? handleDrop : () => {}} />
        {blocks.map((block, idx) => (
          <React.Fragment key={block.id}>
            <Box
              draggable={isEditing}
              onDragStart={(e: React.DragEvent) => {
                const src: DragSource & { crossRowMove?: (toRow: number, toInsert: number) => void } = { type: 'wall', rowIndex, index: idx };
                dragRef.current = src;
                onWallDragStart(src);
                const el = e.currentTarget as HTMLElement;
                const clone = el.cloneNode(true) as HTMLElement;
                clone.style.position = 'fixed';
                clone.style.top = '-9999px';
                clone.style.left = '-9999px';
                clone.style.margin = '0';
                clone.style.pointerEvents = 'none';
                document.body.appendChild(clone);
                e.dataTransfer.setDragImage(clone, el.offsetWidth / 2, el.offsetHeight / 2);
                setTimeout(() => document.body.removeChild(clone), 0);
                e.dataTransfer.effectAllowed = 'move';
              }}
              onDragEnd={() => { dragRef.current = null; }}
              sx={{
                width: SQUARE_SIZE,
                height: SQUARE_SIZE,
                backgroundColor: block.color ?? 'primary.main',
                borderRadius: 1,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 1,
                cursor: isEditing ? 'grab' : 'default',
                userSelect: 'none',
              }}
            >
              <Box
                sx={{
                  color: '#000',
                  textAlign: 'left',
                  lineHeight: 1.6,
                  fontStyle: 'normal',
                  fontSize: '0.75rem',
                  fontFamily: 'inherit',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  whiteSpace: 'pre-wrap',
                  userSelect: 'none',
                  width: '100%',
                  overflow: 'hidden',
                  '& *': { color: '#000' },
                  '& p': { m: 0, lineHeight: 1.6, fontSize: '0.75rem', color: '#000' },
                  '& strong': { fontWeight: 700 },
                  '& em': { fontStyle: 'italic' },
                  '& a': { color: '#000 !important', textDecoration: 'underline', textDecorationColor: '#000' },
                }}
              >
                <MarkdownRenderer
                  components={{
                    a: ({ href, children }: any) => (
                      <Tooltip
                        title={
                          <span>
                            <strong>Full link:</strong><br />{href}
                          </span>
                        }
                        placement="top"
                        arrow
                      >
                        <a href={href} target="_blank" rel="noopener noreferrer">
                          {children}
                        </a>
                      </Tooltip>
                    ),
                  }}
                >
                  {prepareForDisplay(block.label)}
                </MarkdownRenderer>
              </Box>
            </Box>
            <DropZone index={idx + 1} active={dragOverIndex === idx + 1} enabled={isEditing} onDragOver={isEditing ? setDragOverIndex : () => {}} onDragLeave={clearDragOver} onDrop={isEditing ? handleDrop : () => {}} />
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

// ─── BlocksContent ────────────────────────────────────────────────────────────

/** Extended DragSource carrying an optional cross-row move callback. */
type DragSourceExt = DragSource & {
  crossRowMove?: (toRowIndex: number, toInsertAt: number) => void;
};

const BlocksContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [isHovering, setIsHovering] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [wallRows, setWallRows] = useState<WallRow[]>([]);
  const [wallLoading, setWallLoading] = useState(true);
  const [blockSequences, setBlockSequences] = useState<BlockSequence[]>([]);
  const [graphData, setGraphData] = useState<BlockNodeGraphResponse | null>(null);
  const [graphLoading, setGraphLoading] = useState(false);
  const [graphError, setGraphError] = useState<string | null>(null);
  const [sequencesLoading, setSequencesLoading] = useState(false);
  const [sequencesError, setSequencesError] = useState<string | null>(null);
  const [invalidDropMessage, setInvalidDropMessage] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [mapGistContent, setMapGistContent] = useState('');
  const [draftMapGistContent, setDraftMapGistContent] = useState('');
  const [isEditingMapGist, setIsEditingMapGist] = useState(false);
  const [isHoveringMapGist, setIsHoveringMapGist] = useState(false);
  const [mapGistLoading, setMapGistLoading] = useState(false);
  const [mapGistError, setMapGistError] = useState<string | null>(null);
  const [isSavingMapGist, setIsSavingMapGist] = useState(false);
  const [phantomDragOver, setPhantomDragOver] = useState(false);
  const phantomLeaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragSourceExt | null>(null);
  const editStartSnapshotRef = useRef<WallRow[]>([]);

  // Derived: all block IDs currently on ANY row — used for predecessor checks.
  const allWallIds = useMemo(
    () => new Set(wallRows.flatMap((r) => r.blocks.map((b) => b.id))),
    [wallRows],
  );

  // Load the persisted wall on mount.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [wallRes, seqRes] = await Promise.all([
          blockService.getBlockWall(),
          blockService.getAllBlockSequences(),
        ]);
        if (cancelled) return;
        const sequences = seqRes.success ? (seqRes.data ?? []) : [];
        if (wallRes.success && wallRes.rows.length > 0) {
          const seqById = new Map(sequences.map((s) => [s.blockSequenceId, s]));
          const rows: WallRow[] = wallRes.rows.map((row) => {
            const blocks: WallBlock[] = [];
            row.refs.forEach((ref) => {
              const [seqId, blockId] = ref.split('#');
              const seq = seqById.get(seqId);
              if (!seq) return;
              const block = seq.blocks.find((b) => b.blockId === blockId);
              if (!block) return;
              blocks.push({
                id: ref,
                label: block.text,
                color: getCategoryHexColor(seq.category ?? ''),
              });
            });
            return { rowKsuid: row.rowKsuid, blocks };
          });
          setWallRows(rows);
        }
      } finally {
        if (!cancelled) setWallLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadGraph = async () => {
      setGraphLoading(true);
      setGraphError(null);
      try {
        const res = await blockService.getBlockNodeGraph();
        if (!cancelled) {
          setGraphData(res);
          if (!res.success) {
            setGraphError(res.error ?? 'Failed to load block graph.');
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Error fetching block graph:', error);
          setGraphError('Failed to load block graph.');
        }
      } finally {
        if (!cancelled) setGraphLoading(false);
      }
    };

    loadGraph();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadMapGist = async () => {
      setMapGistLoading(true);
      setMapGistError(null);
      try {
        const res = await mapGistService.getMapGist();
        if (!cancelled) {
          if (res.success && res.data) {
            setMapGistContent(res.data.content);
          } else {
            setMapGistError(res.error ?? 'Failed to load map gist.');
          }
        }
      } catch {
        if (!cancelled) {
          setMapGistError('Failed to load map gist.');
        }
      } finally {
        if (!cancelled) setMapGistLoading(false);
      }
    };

    void loadMapGist();
    return () => { cancelled = true; };
  }, []);

  const handleStartMapGistEdit = useCallback(() => {
    setDraftMapGistContent(mapGistContent);
    setMapGistError(null);
    setIsEditingMapGist(true);
  }, [mapGistContent]);

  const handleCancelMapGistEdit = useCallback(() => {
    setDraftMapGistContent(mapGistContent);
    setMapGistError(null);
    setIsEditingMapGist(false);
  }, [mapGistContent]);

  const handleSaveMapGist = useCallback(async () => {
    if (isSavingMapGist) {
      return;
    }

    setIsSavingMapGist(true);
    setMapGistError(null);
    try {
      const res = await mapGistService.updateMapGist({ content: draftMapGistContent });
      if (!res.success || !res.data) {
        setMapGistError(res.error ?? 'Failed to save map gist.');
        return;
      }
      setMapGistContent(res.data.content);
      setIsEditingMapGist(false);
    } catch {
      setMapGistError('Failed to save map gist.');
    } finally {
      setIsSavingMapGist(false);
    }
  }, [draftMapGistContent, isSavingMapGist]);

  /**
   * handleWallChange — called when a single row's block list changes.
   * If the resulting blocks array is empty and there is more than one row,
   * the row is removed entirely.
   */
  const handleWallChange = useCallback((rowIndex: number, next: WallBlock[]) => {
    setWallRows((prev) => {
      if (next.length === 0 && prev.length >= 2) {
        return prev.filter((_, i) => i !== rowIndex);
      }
      return prev.map((r, i) => i === rowIndex ? { ...r, blocks: next } : r);
    });
  }, []);

  /**
   * handleCrossRowMove — moves a block from its source row to a target row.
   * Removes the source row if it becomes empty (and there are >= 2 rows).
   */
  const handleCrossRowMove = useCallback(
    (fromRowIndex: number, fromBlockIndex: number, toRowIndex: number, toInsertAt: number) => {
      setWallRows((prev) => {
        const next = prev.map((r) => ({ ...r, blocks: [...r.blocks] }));
        const [moved] = next[fromRowIndex].blocks.splice(fromBlockIndex, 1);
        // Insert into the target row using the original index (before any filtering).
        // We filter out the source row afterwards — since they're different rows, order is safe.
        next[toRowIndex].blocks.splice(toInsertAt, 0, moved);
        // Remove source row if now empty and we have > 1 rows.
        return next.filter((r, i) => !(i === fromRowIndex && r.blocks.length === 0 && next.length >= 2));
      });
    },
    [],
  );

  const removeFromWall = useCallback(() => {
    const src = dragRef.current;
    if (!src || src.type !== 'wall') return;
    const { rowIndex: fromRow, index: fromIdx } = src;
    dragRef.current = null;
    setWallRows((prev) => {
      const row = prev[fromRow];
      if (!row) return prev;
      const next = row.blocks.filter((_, i) => i !== fromIdx);
      if (next.length === 0 && prev.length >= 2) {
        return prev.filter((_, i) => i !== fromRow);
      }
      return prev.map((r, i) => i === fromRow ? { ...r, blocks: next } : r);
    });
  }, [dragRef]);

  const hasChanges =
    JSON.stringify(wallRows) !== JSON.stringify(editStartSnapshotRef.current);

  const handleEnterEdit = async () => {
    editStartSnapshotRef.current = wallRows.map((r) => ({ ...r, blocks: [...r.blocks] }));
    setIsEditing(true);
    setSequencesLoading(true);
    setSequencesError(null);
    try {
      const res = await blockService.getAllBlockSequences();
      if (res.success) {
        setBlockSequences(res.data ?? []);
      } else {
        setSequencesError(res.error ?? 'Failed to load block sequences');
      }
    } catch {
      setSequencesError('Network error loading block sequences');
    } finally {
      setSequencesLoading(false);
    }
  };

  const handleSave = useCallback(async () => {
    setSaveError(null);
    const inputRows = wallRows.map((r) => ({
      rowKsuid: r.rowKsuid,
      refs: r.blocks.map((b) => b.id),
    }));
    const res = await blockService.saveBlockWall(inputRows);
    if (!res.success) {
      setSaveError(res.error ?? 'Failed to save block wall');
      return;
    }
    // Update row KSUIDs with server-assigned values for new rows.
    if (res.rows && res.rows.length === wallRows.length) {
      setWallRows((prev) =>
        prev.map((r, i) => ({ ...r, rowKsuid: res.rows[i].rowKsuid }))
      );
    }
    setIsEditing(false);
  }, [wallRows]);

  const handleCancel = useCallback(() => {
    setWallRows(editStartSnapshotRef.current);
    setIsEditing(false);
  }, []);

  // Click outside → save
  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (!isEditing) return;
      if (!containerRef.current) return;
      if (containerRef.current.contains(e.target as Node)) return;
      if (document.querySelector('[role="dialog"]')) return;
      handleSave();
    };
    if (isEditing) document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [isEditing, handleSave]);

  // Escape → cancel
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') handleCancel(); };
    if (isEditing) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isEditing, handleCancel]);

  const openHint = () => {
    if (!location.pathname.endsWith('/hint')) {
      navigate(location.pathname + '/hint');
    }
  };

  /** Handle a drop onto the PhantomRow (creates a new row at the end). */
  const handlePhantomDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (phantomLeaveTimer.current) {
      clearTimeout(phantomLeaveTimer.current);
      phantomLeaveTimer.current = null;
    }
    setPhantomDragOver(false);
    const src = dragRef.current;
    if (!src) return;

    if (src.type === 'source') {
      // Predecessor check against all wall blocks.
      const [seqId, blockId] = src.block.id.split('#');
      if (allWallIds.has(src.block.id)) { dragRef.current = null; return; }
      const seq = blockSequences.find((s) => s.blockSequenceId === seqId);
      if (seq) {
        const blockIdx = seq.blocks.findIndex((b) => b.blockId === blockId);
        for (let i = 0; i < blockIdx; i++) {
          const predId = `${seqId}#${seq.blocks[i].blockId}`;
          if (!allWallIds.has(predId)) {
            setInvalidDropMessage(`You must add "${seq.blocks[i].text || `Block ${i + 1}`}" to the wall before this block.`);
            dragRef.current = null;
            return;
          }
        }
      }
      setWallRows((prev) => [...prev, { rowKsuid: null, blocks: [src.block] }]);
    } else {
      // Wall block dragged onto phantom — move it to a new row at the end.
      const { rowIndex: fromRow, index: fromIdx } = src;
      setWallRows((prev) => {
        const srcRow = prev[fromRow];
        if (!srcRow) return prev;
        const moved = srcRow.blocks[fromIdx];
        if (!moved) return prev;
        const updatedSrc = srcRow.blocks.filter((_, i) => i !== fromIdx);
        const next = prev.map((r, i) => i === fromRow ? { ...r, blocks: updatedSrc } : r);
        // Remove source row if it became empty and we'd still have rows after adding the new one.
        const withNewRow = [...next, { rowKsuid: null, blocks: [moved] }];
        return withNewRow.filter((r, i) => !(i === fromRow && r.blocks.length === 0 && withNewRow.length >= 2));
      });
    }
    dragRef.current = null;
  }, [allWallIds, blockSequences, dragRef]);

  // Attach the cross-row move callback onto dragRef when starting a wall drag.
  // BlockWall calls dragRef.current.crossRowMove(toRow, toInsert) for cross-row drops.
  const attachCrossRowMove = useCallback((src: DragSource & { crossRowMove?: (toRow: number, toInsert: number) => void }) => {
    if (src.type === 'wall') {
      src.crossRowMove = (toRowIndex: number, toInsertAt: number) => {
        handleCrossRowMove(src.rowIndex, src.index, toRowIndex, toInsertAt);
      };
    }
  }, [handleCrossRowMove]);

  return (
    <Card ref={containerRef} sx={{ overflow: 'hidden' }}>
      <CardContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Block Wall MVP
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Reference nodes of type Block and all of their relationships in my AuraDB instance for the latest data.
          </Typography>
          <Accordion disableGutters elevation={0} sx={{ bgcolor: 'transparent', border: '1px solid rgba(148, 163, 184, 0.25)', borderRadius: 1, mb: 1, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1.5 }}>
              <Typography variant="body2">Query to show path: Save money.</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, px: 1.5, pb: 1.5 }}>
              <Box
                component="code"
                sx={{
                  display: 'block',
                  p: 1,
                  borderRadius: 1,
                  bgcolor: '#111827',
                  color: '#F8FAFC',
                  border: '1px solid rgba(148, 163, 184, 0.4)',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {'MATCH (root:Block {About: "Root of PATH:SAVE_MONEY"})\n\n// Find complete successful paths to any goal\nMATCH goalPath = (root)-[*1..]->(goal:Block)\nWHERE goal.isGoal = "yes"\n  AND all(n IN nodes(goalPath) WHERE n:Block)\n\nWITH DISTINCT goalPath\n\n// Look at every Block along the successful path\nUNWIND nodes(goalPath) AS pathNode\n\n// Show at most one-hop side connections in either direction\nOPTIONAL MATCH (pathNode)-[sideRelationship]-(sideNode:Block)\n\nWHERE\n    // Don\'t count nodes already on this successful path\n    NOT sideNode IN nodes(goalPath)\n\n    // Only show the side node when it does not eventually\n    // lead forward to any goal\n    AND NOT EXISTS {\n        MATCH sidePath = (sideNode)-[*0..]->(sideGoal:Block)\n        WHERE sideGoal.isGoal = "yes"\n          AND all(n IN nodes(sidePath) WHERE n:Block)\n    }\n\nRETURN DISTINCT\n    goalPath,\n    sideRelationship,\n    sideNode;'}
              </Box>
            </AccordionDetails>
          </Accordion>
          <Accordion disableGutters elevation={0} sx={{ bgcolor: 'transparent', border: '1px solid rgba(148, 163, 184, 0.25)', borderRadius: 1, mb: 1, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1.5 }}>
              <Typography variant="body2">Query to show path: Make money.</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, px: 1.5, pb: 1.5 }}>
              <Box
                component="code"
                sx={{
                  display: 'block',
                  p: 1,
                  borderRadius: 1,
                  bgcolor: '#111827',
                  color: '#F8FAFC',
                  border: '1px solid rgba(148, 163, 184, 0.4)',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {'MATCH (root:Block {About: "Root of PATH:SAVE_MONEY"})\n\n// Find complete successful paths to any goal\nMATCH goalPath = (root)-[*1..]->(goal:Block)\nWHERE goal.isGoal = "yes"\n  AND all(n IN nodes(goalPath) WHERE n:Block)\n\nWITH DISTINCT goalPath\n\n// Look at every Block along the successful path\nUNWIND nodes(goalPath) AS pathNode\n\n// Show at most one-hop side connections in either direction\nOPTIONAL MATCH (pathNode)-[sideRelationship]-(sideNode:Block)\n\nWHERE\n    // Don\'t count nodes already on this successful path\n    NOT sideNode IN nodes(goalPath)\n\n    // Only show the side node when it does not eventually\n    // lead forward to any goal\n    AND NOT EXISTS {\n        MATCH sidePath = (sideNode)-[*0..]->(sideGoal:Block)\n        WHERE sideGoal.isGoal = "yes"\n          AND all(n IN nodes(sidePath) WHERE n:Block)\n    }\n\nRETURN DISTINCT\n    goalPath,\n    sideRelationship,\n    sideNode;'}
              </Box>
            </AccordionDetails>
          </Accordion>
          <Accordion disableGutters elevation={0} sx={{ bgcolor: 'transparent', border: '1px solid rgba(148, 163, 184, 0.25)', borderRadius: 1, mb: 1, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1.5 }}>
              <Typography variant="body2">Query to show path: Make a name.</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, px: 1.5, pb: 1.5 }}>
              <Box
                component="code"
                sx={{
                  display: 'block',
                  p: 1,
                  borderRadius: 1,
                  bgcolor: '#111827',
                  color: '#F8FAFC',
                  border: '1px solid rgba(148, 163, 184, 0.4)',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {'MATCH (root:Block {About: "Root of PATH:MAKE_A_NAME"})\n\n// Find complete successful paths to any goal\nMATCH goalPath = (root)-[*1..]->(goal:Block)\nWHERE goal.isGoal = "yes"\n  AND all(n IN nodes(goalPath) WHERE n:Block)\n\nWITH DISTINCT goalPath\n\n// Look at every Block along the successful path\nUNWIND nodes(goalPath) AS pathNode\n\n// Show at most one-hop side connections in either direction\nOPTIONAL MATCH (pathNode)-[sideRelationship]-(sideNode:Block)\n\nWHERE\n    // Don\'t count nodes already on this successful path\n    NOT sideNode IN nodes(goalPath)\n\n    // Only show the side node when it does not eventually\n    // lead forward to any goal\n    AND NOT EXISTS {\n        MATCH sidePath = (sideNode)-[*0..]->(sideGoal:Block)\n        WHERE sideGoal.isGoal = "yes"\n          AND all(n IN nodes(sidePath) WHERE n:Block)\n    }\n\nRETURN DISTINCT\n    goalPath,\n    sideRelationship,\n    sideNode;'}
              </Box>
            </AccordionDetails>
          </Accordion>
          <Accordion disableGutters elevation={0} sx={{ bgcolor: 'transparent', border: '1px solid rgba(148, 163, 184, 0.25)', borderRadius: 1, mb: 1, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1.5 }}>
              <Typography variant="body2">Query to show path: Spend time with people I love.</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, px: 1.5, pb: 1.5 }}>
              <Box
                component="code"
                sx={{
                  display: 'block',
                  p: 1,
                  borderRadius: 1,
                  bgcolor: '#111827',
                  color: '#F8FAFC',
                  border: '1px solid rgba(148, 163, 184, 0.4)',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {'MATCH (root:Block {About: "Root of PATH:SPEND_TIME_WITH_PEOPLE_I_LOVE"})\n\n// Find complete successful paths to any goal\nMATCH goalPath = (root)-[*1..]->(goal:Block)\nWHERE goal.isGoal = "yes"\n  AND all(n IN nodes(goalPath) WHERE n:Block)\n\nWITH DISTINCT goalPath\n\n// Look at every Block along the successful path\nUNWIND nodes(goalPath) AS pathNode\n\n// Show at most one-hop side connections in either direction\nOPTIONAL MATCH (pathNode)-[sideRelationship]-(sideNode:Block)\n\nWHERE\n    // Don\'t count nodes already on this successful path\n    NOT sideNode IN nodes(goalPath)\n\n    // Only show the side node when it does not eventually\n    // lead forward to any goal\n    AND NOT EXISTS {\n        MATCH sidePath = (sideNode)-[*0..]->(sideGoal:Block)\n        WHERE sideGoal.isGoal = "yes"\n          AND all(n IN nodes(sidePath) WHERE n:Block)\n    }\n\nRETURN DISTINCT\n    goalPath,\n    sideRelationship,\n    sideNode;'}
              </Box>
            </AccordionDetails>
          </Accordion>
          <Accordion disableGutters elevation={0} sx={{ bgcolor: 'transparent', border: '1px solid rgba(148, 163, 184, 0.25)', borderRadius: 1, mb: 1, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1.5 }}>
              <Typography variant="body2">Query to show path: Build things.</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, px: 1.5, pb: 1.5 }}>
              <Box
                component="code"
                sx={{
                  display: 'block',
                  p: 1,
                  borderRadius: 1,
                  bgcolor: '#111827',
                  color: '#F8FAFC',
                  border: '1px solid rgba(148, 163, 184, 0.4)',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {'MATCH (root:Block {About: "Root of PATH:BUILD_THINGS"})\n\n// Find complete successful paths to any goal\nMATCH goalPath = (root)-[*1..]->(goal:Block)\nWHERE goal.isGoal = "yes"\n  AND all(n IN nodes(goalPath) WHERE n:Block)\n\nWITH DISTINCT goalPath\n\n// Look at every Block along the successful path\nUNWIND nodes(goalPath) AS pathNode\n\n// Show at most one-hop side connections in either direction\nOPTIONAL MATCH (pathNode)-[sideRelationship]-(sideNode:Block)\n\nWHERE\n    // Don\'t count nodes already on this successful path\n    NOT sideNode IN nodes(goalPath)\n\n    // Only show the side node when it does not eventually\n    // lead forward to any goal\n    AND NOT EXISTS {\n        MATCH sidePath = (sideNode)-[*0..]->(sideGoal:Block)\n        WHERE sideGoal.isGoal = "yes"\n          AND all(n IN nodes(sidePath) WHERE n:Block)\n    }\n\nRETURN DISTINCT\n    goalPath,\n    sideRelationship,\n    sideNode;'}
              </Box>
            </AccordionDetails>
          </Accordion>
          <Accordion disableGutters elevation={0} sx={{ bgcolor: 'transparent', border: '1px solid rgba(148, 163, 184, 0.25)', borderRadius: 1, mb: 1, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1.5 }}>
              <Typography variant="body2">Query to show path: Enjoy life.</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, px: 1.5, pb: 1.5 }}>
              <Box
                component="code"
                sx={{
                  display: 'block',
                  p: 1,
                  borderRadius: 1,
                  bgcolor: '#111827',
                  color: '#F8FAFC',
                  border: '1px solid rgba(148, 163, 184, 0.4)',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {'MATCH (root:Block {About: "Root of PATH:ENJOY_LIFE"})\n\n// Find complete successful paths to any goal\nMATCH goalPath = (root)-[*1..]->(goal:Block)\nWHERE goal.isGoal = "yes"\n  AND all(n IN nodes(goalPath) WHERE n:Block)\n\nWITH DISTINCT goalPath\n\n// Look at every Block along the successful path\nUNWIND nodes(goalPath) AS pathNode\n\n// Show at most one-hop side connections in either direction\nOPTIONAL MATCH (pathNode)-[sideRelationship]-(sideNode:Block)\n\nWHERE\n    // Don\'t count nodes already on this successful path\n    NOT sideNode IN nodes(goalPath)\n\n    // Only show the side node when it does not eventually\n    // lead forward to any goal\n    AND NOT EXISTS {\n        MATCH sidePath = (sideNode)-[*0..]->(sideGoal:Block)\n        WHERE sideGoal.isGoal = "yes"\n          AND all(n IN nodes(sidePath) WHERE n:Block)\n    }\n\nRETURN DISTINCT\n    goalPath,\n    sideRelationship,\n    sideNode;'}
              </Box>
            </AccordionDetails>
          </Accordion>
          <Accordion disableGutters elevation={0} sx={{ bgcolor: 'transparent', border: '1px solid rgba(148, 163, 184, 0.25)', borderRadius: 1, mb: 1, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1.5 }}>
              <Typography variant="body2">Query to show blocks:</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, px: 1.5, pb: 1.5 }}>
              <Box
                component="code"
                sx={{
                  display: 'block',
                  p: 1,
                  borderRadius: 1,
                  bgcolor: '#111827',
                  color: '#F8FAFC',
                  border: '1px solid rgba(148, 163, 184, 0.4)',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {'MATCH (block:Block)\nOPTIONAL MATCH (block)-[r]-(other)\nRETURN block, r, other;'}
              </Box>
            </AccordionDetails>
          </Accordion>
          <Accordion disableGutters elevation={0} sx={{ bgcolor: 'transparent', border: '1px solid rgba(148, 163, 184, 0.25)', borderRadius: 1, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1.5 }}>
              <Typography variant="body2">Query to show block goals:</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, px: 1.5, pb: 1.5 }}>
              <Box
                component="code"
                sx={{
                  display: 'block',
                  p: 1,
                  borderRadius: 1,
                  bgcolor: '#111827',
                  color: '#F8FAFC',
                  border: '1px solid rgba(148, 163, 184, 0.4)',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {'MATCH (root:Block {About: "Root of Open Paths"})\n\n// Find every successful path from the root to a goal\nMATCH goalPath = (root)-[*1..]->(goal:Block {isGoal: "yes"})\n\n// Ensure the traversed nodes are Blocks\nWHERE all(n IN nodes(goalPath) WHERE n:Block)\n\nWITH DISTINCT goalPath\n\n// Examine every node along each successful path\nUNWIND nodes(goalPath) AS pathNode\n\n// Find one-hop branches from those nodes\nOPTIONAL MATCH branchPath = (pathNode)-[]->(branch:Block)\n\nWHERE\n    // Don\'t treat another node on this same successful path as a dead branch\n    NOT branch IN nodes(goalPath)\n\n    // Only include this one-hop branch if it cannot eventually reach a goal\n    AND NOT EXISTS {\n        MATCH branchToGoal = (branch)-[*0..]->(branchGoal:Block {isGoal: "yes"})\n        WHERE all(n IN nodes(branchToGoal) WHERE n:Block)\n    }\n\nRETURN DISTINCT\n    goalPath,\n    branchPath;'}
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* Header */}
        <Box
          sx={{ position: 'relative', mb: isEditing ? 0.5 : 1 }}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          <Typography variant="h6">Blocks</Typography>
          {isHovering && (
            <IconButton
              size="small"
              onClick={() => (isEditing ? handleSave() : handleEnterEdit())}
              sx={{
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#32CA1D',
                bgcolor: isEditing ? 'rgba(50, 202, 29, 0.1)' : 'transparent',
                '&:hover': { bgcolor: 'rgba(50, 202, 29, 0.1)' },
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {isEditing && (
          <>
            {/* Subtitle */}
            <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary', fontStyle: 'italic' }}>
              Drag blocks to create a Block Wall.{' '}
              <Link
                component="button"
                variant="body2"
                onClick={openHint}
                sx={{ color: 'primary.main', cursor: 'pointer', fontStyle: 'italic', verticalAlign: 'baseline' }}
              >
                Blocks are &ldquo;big&rdquo;
              </Link>
              , so it&rsquo;s important to order them correctly.
            </Typography>

            {/* Source rows — also a drop target to remove blocks from the wall */}
            <Box
              onDragOver={(e) => {
                if (isEditing && dragRef.current?.type === 'wall') { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }
              }}
              onDrop={(e) => {
                if (!isEditing) return;
                e.preventDefault();
                removeFromWall();
              }}
              sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, overflow: 'hidden', minWidth: 0, mb: 1.5 }}
            >
              {sequencesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={24} sx={{ color: '#32CA1D' }} />
                </Box>
              ) : sequencesError ? (
                <Alert severity="error" sx={{ mb: 1 }}>{sequencesError}</Alert>
              ) : blockSequences.length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{ color: 'grey.500', fontStyle: 'italic', py: 2, textAlign: 'center' }}
                >
                  No block sequences have been created yet.
                </Typography>
              ) : (
                blockSequences.map((seq) => (
                  <SquareRow key={seq.blockSequenceId} sequence={seq} wallBlockIds={allWallIds} dragRef={dragRef} />
                ))
              )}
            </Box>

            {/* Save / Cancel */}
            <Box display="flex" gap={1} justifyContent="flex-end" sx={{ mb: 2 }}>
              {saveError && (
                <Alert severity="error" sx={{ flex: 1, mr: 1 }} onClose={() => setSaveError(null)}>
                  {saveError}
                </Alert>
              )}
              <Button
                variant="outlined"
                size="small"
                onClick={handleCancel}
                startIcon={<CloseIcon />}
                sx={{
                  borderColor: 'grey.600',
                  color: 'grey.300',
                  '&:hover': { borderColor: 'grey.500', bgcolor: 'rgba(255, 255, 255, 0.05)' },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleSave}
                disabled={!hasChanges}
                startIcon={<CheckIcon />}
                sx={{
                  bgcolor: hasChanges ? '#32CA1D' : undefined,
                  color: hasChanges ? '#000' : undefined,
                  '&:hover': { bgcolor: hasChanges ? '#28A518' : undefined },
                  '&.Mui-disabled': { opacity: 0.38 },
                }}
              >
                Save
              </Button>
            </Box>

            <Divider sx={{ borderColor: 'primary.main', borderBottomWidth: 2, mb: 1.5 }} />
          </>
        )}

        {/* Block Wall — always visible */}
        <Box sx={{ backgroundColor: 'rgba(50, 202, 29, 0.12)', borderRadius: 1, pt: 2.5, pb: isEditing ? 0 : 1.5 }}>
          {wallLoading ? (
            <Box sx={{ display: 'flex', gap: 1, px: 2, alignItems: 'center', minHeight: SQUARE_SIZE }}>
              {[0, 1, 2].map((i) => (
                <Box key={i} sx={{ width: SQUARE_SIZE, height: SQUARE_SIZE, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.08)', flexShrink: 0 }} />
              ))}
            </Box>
          ) : (
            <>
              {/* Rows — gap only between rows, phantom is outside this flex container */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {!isEditing && wallRows.length > 3 && (
                  <Alert severity="warning" sx={{ mx: 2, mb: 0.5 }}>
                    Too many rows in the Block Wall—reprioritize, say no, or delegate!
                  </Alert>
                )}
                {wallRows.map((row, rowIdx) => (
                  <BlockWall
                    key={row.rowKsuid ?? `new-${rowIdx}`}
                    rowIndex={rowIdx}
                    blocks={row.blocks}
                    onBlocksChange={handleWallChange}
                    dragRef={dragRef}
                    blockSequences={blockSequences}
                    allWallIds={allWallIds}
                    onInvalidDrop={setInvalidDropMessage}
                    onWallDragStart={attachCrossRowMove}
                    isEditing={isEditing}
                  />
                ))}
              </Box>

              {/* Phantom drop zone — outside the gap column so no extra gap is added above it */}
              {isEditing && (
                <Box
                  onDragEnter={(e) => {
                    const src = dragRef.current;
                    if (!src) return;
                    if (src.type === 'source' || src.type === 'wall') {
                      e.preventDefault();
                      if (phantomLeaveTimer.current) {
                        clearTimeout(phantomLeaveTimer.current);
                        phantomLeaveTimer.current = null;
                      }
                      setPhantomDragOver(true);
                    }
                  }}
                  onDragOver={(e) => {
                    const src = dragRef.current;
                    if (!src) return;
                    if (src.type === 'source' || src.type === 'wall') {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                    }
                  }}
                  onDragLeave={() => {
                    phantomLeaveTimer.current = setTimeout(() => {
                      setPhantomDragOver(false);
                      phantomLeaveTimer.current = null;
                    }, 80);
                  }}
                  onDrop={handlePhantomDrop}
                  sx={{
                    width: '100%',
                    height: phantomDragOver ? SQUARE_SIZE : 12,
                    border: phantomDragOver
                      ? '2px dashed rgba(50, 202, 29, 0.65)'
                      : '2px dashed transparent',
                    borderRadius: 1,
                    transition: 'height 0.15s ease, border-color 0.15s ease',
                  }}
                />
              )}
            </>
          )}
        </Box>

        <Box sx={{ mt: 2 }}>
          <BlockNodeGraph graph={graphData ?? { success: true, nodes: [], edges: [] }} title="Block Nodes" />
          {graphLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <CircularProgress size={18} sx={{ color: '#32CA1D' }} />
              <Typography variant="body2" color="text.secondary">Loading block graph…</Typography>
            </Box>
          )}
          {graphError && (
            <Alert severity="error" sx={{ mt: 1 }}>{graphError}</Alert>
          )}
        </Box>

        <Box
          sx={{ mt: 2 }}
          onMouseEnter={() => setIsHoveringMapGist(true)}
          onMouseLeave={() => setIsHoveringMapGist(false)}
        >
          <Box sx={{ position: 'relative', mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Map Gist</Typography>
            <HoverActionIconButton
              show={isHoveringMapGist && !isEditingMapGist}
              ariaLabel="Edit map gist"
              onClick={handleStartMapGistEdit}
              sx={{
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#32CA1D',
                '&:hover': { bgcolor: 'rgba(50, 202, 29, 0.1)' },
              }}
            >
              <EditIcon fontSize="small" />
            </HoverActionIconButton>
          </Box>

          {mapGistLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 80 }}>
              <CircularProgress size={18} sx={{ color: '#32CA1D' }} />
              <Typography variant="body2" color="text.secondary">Loading map gist…</Typography>
            </Box>
          ) : mapGistError ? (
            <Alert severity="error">{mapGistError}</Alert>
          ) : isEditingMapGist ? (
            <Box sx={{ p: 2, bgcolor: '#1A1A1A', borderRadius: 1 }}>
              <TextField
                fullWidth
                multiline
                minRows={8}
                value={draftMapGistContent}
                onChange={(e) => setDraftMapGistContent(e.target.value)}
                placeholder="Write a map gist in markdown"
                disabled={isSavingMapGist}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#000',
                    fontFamily: 'monospace',
                    '& fieldset': { borderColor: '#32CA1D' },
                    '&:hover fieldset': { borderColor: '#32CA1D' },
                    '&.Mui-focused fieldset': { borderColor: '#32CA1D' },
                  },
                }}
              />
              <Box display="flex" gap={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCancelMapGistEdit}
                  disabled={isSavingMapGist}
                  startIcon={<CloseIcon />}
                  sx={{ borderColor: 'grey.600', color: 'grey.300', '&:hover': { borderColor: 'grey.500', bgcolor: 'rgba(255,255,255,0.05)' } }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => void handleSaveMapGist()}
                  disabled={isSavingMapGist || draftMapGistContent === mapGistContent}
                  startIcon={isSavingMapGist ? <CircularProgress size={16} /> : <CheckIcon />}
                  sx={{ bgcolor: '#32CA1D', color: '#000', '&:hover': { bgcolor: '#28A518' }, '&.Mui-disabled': { bgcolor: 'grey.700', color: 'grey.500' } }}
                >
                  {isSavingMapGist ? 'Saving...' : 'Save'}
                </Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{ p: 2, bgcolor: '#1A1A1A', borderRadius: 1, minHeight: 80 }}>
              {mapGistContent.trim() ? (
                <MarkdownText>{mapGistContent}</MarkdownText>
              ) : (
                <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                  No map gist yet.
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </CardContent>
      <Snackbar
        open={!!invalidDropMessage}
        autoHideDuration={4000}
        onClose={() => setInvalidDropMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning" onClose={() => setInvalidDropMessage(null)} sx={{ width: '100%' }}>
          {invalidDropMessage}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default BlocksContent;
